import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EditOutlined } from '@ant-design/icons';
import { useAtomValue } from 'jotai';
import { Button, Checkbox, Popover, Space, Tooltip } from 'antd';
import { supportedMetricsSelector } from '@store/common/selectors';
import { setSummaryWidgets } from '@store/summaryWidgets/actions';
import { filterNAMetricsAtom } from '@src/atoms/page';
import { summaryWidgetsSelector } from '@store/summaryWidgets/selector';
import { AUDIENCE_METRICS, Widget, WIDGET_TYPES } from '../constants';
import { CheckboxWrapper } from '@components/top-widget-panel/spi-panel/styled';
import { getPopoverOptions, getSavedOptions, isDisabled } from './utils';

const EditMetrics = () => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const allowedMetrics: Record<string, any> = useSelector(supportedMetricsSelector);
  const metricToNA: string[] = useAtomValue(filterNAMetricsAtom);
  const summaryWidgets = useSelector(summaryWidgetsSelector);
  const savedSummaryWidgets = summaryWidgets.widgets;

  const savedSummaryOptions = getSavedOptions(savedSummaryWidgets, allowedMetrics);
  const [summaryOptions, setSummaryOptions] = useState(savedSummaryOptions);
  const audienceMetrics = [...AUDIENCE_METRICS];
  const metricOptions = getPopoverOptions(allowedMetrics);

  const onMetricToggle = (metric) => (event) => {
    const checked = event.target.checked;
    if (checked) {
      setSummaryOptions([...summaryOptions, metric]);
    } else {
      setSummaryOptions(summaryOptions.filter((item) => item !== metric));
    }
  };

  const handleClose = () => {
    setSummaryOptions(savedSummaryOptions);
    setOpen(false);
  };

  // filtering out metrices from widget keys
  const summaryMetrics = summaryOptions.filter((option) => option in allowedMetrics);

  const handleApply = () => {
    const newSummaryWidgets: Widget[] = [];

    savedSummaryWidgets.forEach((widget) => {
      if (
        summaryOptions.includes(widget.widgetKey) &&
        widget.widgetKey !== WIDGET_TYPES.audienceMetrics.name
      ) {
        newSummaryWidgets.push({ ...widget, enabled: true });
      } else if (widget.widgetKey === WIDGET_TYPES.audienceMetrics.name) {
        if (summaryMetrics.some((m) => audienceMetrics.includes(m))) {
          newSummaryWidgets.push({
            ...widget,
            enabled: true,
            config: { ...widget.config, metrics: summaryMetrics },
          });
        } else {
          newSummaryWidgets.push({
            ...widget,
            enabled: false,
            config: { ...widget.config, metrics: [] }, // disable the widget and pop all metrics if no matches found
          });
        }
      } else {
        newSummaryWidgets.push({ ...widget, enabled: false }); // disable the widget if not found
      }
    });

    dispatch(
      setSummaryWidgets({
        type: summaryWidgets.type,
        widgets: newSummaryWidgets,
      }),
    );
    setOpen(false);
  };

  const Content = ({ options }) => {
    return (
      <>
        <CheckboxWrapper>
          {options.map((option) => {
            const disabled = isDisabled(savedSummaryWidgets, option.name, metricToNA);
            return (
              <Tooltip key={option.name} title={disabled ? 'Not Applicable' : ''}>
                <Checkbox
                  key={option.name}
                  onChange={onMetricToggle(option.name)}
                  style={{ width: '250px', margin: 0, paddingBottom: '3px' }}
                  checked={summaryOptions.includes(option.name)}
                  disabled={disabled}
                >
                  <span>{option.displayName}</span>
                  {option.subscript ? <span>{option.subscript}</span> : ''}
                </Checkbox>
              </Tooltip>
            );
          })}
        </CheckboxWrapper>

        <div style={{ display: 'flex', justifyContent: 'end' }}>
          <Button onClick={handleClose}>{'Cancel'}</Button>
          <Button
            style={{
              backgroundColor: '#215bff',
              color: '#fff',
              marginLeft: '10px',
            }}
            onClick={handleApply}
          >
            {'Apply'}
          </Button>
        </div>
      </>
    );
  };

  const handlePopoverOpen = (value: boolean) => {
    setSummaryOptions(savedSummaryOptions);
    setOpen(value);
  };

  return (
    <Space wrap style={{ margin: '0px 5px', padding: '3px', fontSize: '1em' }}>
      <Popover
        content={<Content options={metricOptions || []} />}
        onOpenChange={handlePopoverOpen}
        open={open}
        destroyOnHidden={true}
        placement="bottomRight"
        title="Select Metrics"
        trigger="click"
        arrow={false}
      >
        <Button icon={<EditOutlined />} />
      </Popover>
    </Space>
  );
};

export default EditMetrics;
